import { caseInsensitiveLocaleCompare } from 'util/sort';

/**
 * The supplier request API responses we get is a array of data objects. These objects, while similar
 * in shape (similar properties), have different data depending on whether the object is meant to
 * describe a "company" or a "person". Properties that don't relate to the company or person in each
 * data object are still set, but set to a null value.
 *
 * These company and person data objects are mixed together in the resulting array. It appears to be
 * the case that a "company" data object will also come before any "person" data object associated
 * with that company.
 *
 * For example: If there is one supplier request available, the API response will be an array with
 * two data object elements. The first element will be a "company" object, and the second element will
 * be a "person" object.
 *
 * --- Transformed Structure ---
 *
 * This helper function will take the original API structure noted above, and return an array where each
 * element is a "company", and the people requesting access from that company are listed as "person" objects
 * under a `company.children` property on the company object.
 *
 * Both the "company" and "person" data in the new structure are simplified, meaning we are only exposing
 * data used by the application, and ignoring several properties returned by the API.
 *
 * Example data structure returned by this utility function:
 *   [
 *     {
 *        ...companyProperties,
 *        children: [
 *          { ...personProperties },
 *          { ...personProperties }
 *          ...
 *        ]
 *     },
 *     ...
 *   ]
 */

export function groupSupplierRequestsByCompany(supplierRequests) {
  if (!Array.isArray(supplierRequests) || !supplierRequests.length) {
    return [];
  }

  const supplierRequestsByCompany = supplierRequests.reduce((requestsByCompany, currentCompanyOrPerson) => {
    // we have a company, create a new top level company element in the resulting array, and return
    if (currentCompanyOrPerson.personkey === null) {
      return requestsByCompany.concat([flattenSupplierRequestCompany(currentCompanyOrPerson)]);
    }

    // else - we have a person, add to children list for correct company
    const matchedCompany = requestsByCompany.find(company => company.companyKey === currentCompanyOrPerson.companykey);

    // map over companies, if current company, add person to children list, otherwise just return company
    return requestsByCompany.map(requestCompany =>
      requestCompany.companyKey !== matchedCompany.companyKey
        ? requestCompany
        : {
            ...requestCompany,
            children: [...requestCompany.children, flattenSupplierRequestPerson(currentCompanyOrPerson, matchedCompany)]
          }
    );
  }, []);

  // sort companies alphabetically before returning
  return supplierRequestsByCompany.sort((requestCompanyA, requestCompanyB) =>
    caseInsensitiveLocaleCompare(requestCompanyA.name, requestCompanyB.name)
  );
}

/**
 * Takes in a raw company object from the supplier request API response, and flattens to
 * a simplified version of the object.
 *
 * @param {Object} supplierRequestCompany
 * @returns {Object} simplified company object
 */
function flattenSupplierRequestCompany(supplierRequestCompany) {
  return {
    children: [],
    companyKey: supplierRequestCompany.companykey,
    name: supplierRequestCompany.companyname,
    open: true
  };
}

/**
 * Takes in a raw person object from the supplier request API response and a flattened company object.
 * The result is a simplified person object.
 *
 * @param {Object} supplierRequestPerson
 * @param {Object} flattenedSupplierRequestCompany
 * @returns simplified person object
 */
function flattenSupplierRequestPerson(supplierRequestPerson, flattenedSupplierRequestCompany) {
  return {
    businessTitle: supplierRequestPerson.businesstitle,
    cellPhone: supplierRequestPerson.cellphone,
    companyKey: flattenedSupplierRequestCompany.companyKey,
    companyName: flattenedSupplierRequestCompany.name,
    email: supplierRequestPerson.emailaddress,
    leaf: true,
    name: `${supplierRequestPerson.firstname} ${supplierRequestPerson.lastname}`,
    personKey: supplierRequestPerson.personkey,
    workPhone: supplierRequestPerson.workphone
  };
}

/**
 * Return the total number of people requesting access to a public bid event.
 *
 * @param {Array} supplierRequestsByCompany - result structure created by `groupSupplierRequestByCompany()`
 * @returns {number} total number of people requesting access to a public bid event
 */
export function getPersonCountForSupplierRequestsByCompany(supplierRequestsByCompany) {
  if (!Array.isArray(supplierRequestsByCompany)) {
    return 0;
  }

  return supplierRequestsByCompany.reduce(
    (totalPersonCount, currentRequestCompany) => totalPersonCount + currentRequestCompany.children.length,
    0
  );
}
